import { NetworkStatus } from '@apollo/client';
import { queryDataToD3Data } from 'hooks/useChartDataFetcher/reshape-data';
import { usePlutoApiQuery } from 'hooks/useQuery/plutoApi';
import { MarketKey } from 'pages/Optimisation/components/ContractedVolume/types';
import {
  GET_ANCILLARY_TRADES,
  GET_BALANCING_MECHANISM_TRADES,
  GET_WHOLESALE_TRADES,
} from 'pages/Optimisation/queries';
import { endOfTradingDay, startOfTradingDay } from 'utils/optimisation';

/*
Lives in its own file as it can get complicated and messy with cache manipulation
*/

export const useChartDataFetcher = (
  site: string | undefined,
  currentDate: Date,
) => {
  const getStart = () => startOfTradingDay(currentDate).toISOString();
  const getEnd = () => endOfTradingDay(currentDate).toISOString();

  const defaultQueryOptions = {
    notifyOnNetworkStatusChange: true,
    skip: !site,
    variables: {
      filter: {
        site: { id: { eq: site } },
        start: { gte: getStart() },
        end: { lte: getEnd() },
      },
    },
  };

  const defaultWholesaleQueryOptions = {
    ...defaultQueryOptions,
    variables: {
      filter: {
        ...defaultQueryOptions.variables.filter,
        market: {
          neq: 'unspecified',
        },
      },
    },
  };

  // Query trade data
  const {
    data: wholesaleData,
    error: wholesaleError,
    loading: wholesaleLoading,
    networkStatus: wholesaleNetworkStatus,
  } = usePlutoApiQuery(GET_WHOLESALE_TRADES, defaultWholesaleQueryOptions);

  const {
    data: ancillaryData,
    error: ancillaryError,
    loading: ancillaryLoading,
    networkStatus: ancillaryNetworkStatus,
  } = usePlutoApiQuery(GET_ANCILLARY_TRADES, defaultQueryOptions);

  const {
    data: balancingMechanismData,
    error: balancingMechanismError,
    loading: bmLoading,
    networkStatus: bmNetworkStatus,
  } = usePlutoApiQuery(GET_BALANCING_MECHANISM_TRADES, {
    ...defaultQueryOptions,
    skip: !site,
  });

  const ancillary = ancillaryData
    ? queryDataToD3Data(MarketKey.Ancillary, ancillaryData)
    : [];

  const wholesale = wholesaleData
    ? queryDataToD3Data(MarketKey.Wholesale, wholesaleData)
    : [];

  const balancingMechanism = balancingMechanismData
    ? queryDataToD3Data(MarketKey.BalancingMechanism, balancingMechanismData)
    : [];

  const isLoading = bmLoading || ancillaryLoading || wholesaleLoading;

  const isInitialLoading =
    isLoading &&
    wholesaleNetworkStatus !== NetworkStatus.refetch &&
    wholesaleNetworkStatus !== NetworkStatus.poll &&
    ancillaryNetworkStatus !== NetworkStatus.refetch &&
    ancillaryNetworkStatus !== NetworkStatus.poll &&
    bmNetworkStatus !== NetworkStatus.refetch &&
    bmNetworkStatus !== NetworkStatus.poll;

  const data = !isInitialLoading
    ? {
        ancillary,
        wholesale,
        balancingMechanism,
      }
    : { ancillary: [], wholesale: [], balancingMechanism: [] };

  const errors = [
    wholesaleError?.message,
    ancillaryError?.message,
    balancingMechanismError?.message,
  ]?.filter((x) => !!x) as Array<string>;

  return {
    data,
    errors,
    isInitialLoading,
  };
};
